import type { PaperWithSong, SunoClip } from './types'

// Cache for loaded data
let papersCache: Map<string, PaperWithSong> | null = null
let sunoCache: Record<string, SunoClip> | null = null
let sunoLoadPromise: Promise<void> | null = null

/**
 * Load a single paper by ID
 * Format: "Poster-117515" -> fetches /data/papers/Poster-117515.json
 */
export async function loadPaper(paperId: string): Promise<PaperWithSong | null> {
  // Check cache first
  if (papersCache?.has(paperId)) {
    return papersCache.get(paperId)!
  }

  try {
    const response = await fetch(`/data/papers/${paperId}.json`)
    if (!response.ok) return null
    
    const paper = await response.json() as PaperWithSong
    
    // Add to cache
    if (!papersCache) papersCache = new Map()
    papersCache.set(paperId, paper)
    
    return paper
  } catch (e) {
    console.error(`Failed to load paper ${paperId}:`, e)
    return null
  }
}

/**
 * Preload suno lookup (call during app init)
 */
export async function preloadSunoLookup(): Promise<void> {
  if (sunoCache) return
  if (sunoLoadPromise) return sunoLoadPromise

  sunoLoadPromise = (async () => {
    try {
      const response = await fetch('/data/suno_lookup.json')
      if (response.ok) {
        sunoCache = await response.json()
      }
    } catch (e) {
      console.error('Failed to load suno lookup:', e)
    }
  })()

  return sunoLoadPromise
}

/**
 * Load Suno data for a paper
 */
export async function loadSuno(paperId: string): Promise<SunoClip | null> {
  await preloadSunoLookup()
  return sunoCache?.[paperId] || null
}

/**
 * Get Suno data synchronously (returns null if not loaded)
 */
export function getSunoSync(paperId: string): SunoClip | null {
  return sunoCache?.[paperId] || null
}

/**
 * Check if suno cache is ready
 */
export function isSunoCacheReady(): boolean {
  return sunoCache !== null
}

/**
 * Convert NeurIPS URL type to our paper ID format
 * "poster" -> "Poster", "invited-talk" -> "Invited Talk", etc.
 */
export function typeToPrefix(type: string): string {
  const mapping: Record<string, string> = {
    'poster': 'Poster',
    'workshop': 'Workshop',
    'tutorial': 'Tutorial',
    'invited-talk': 'Invited Talk',
  }
  return mapping[type.toLowerCase()] || type
}

/**
 * Convert paper ID format to URL type
 * "Poster" -> "poster", "Invited Talk" -> "invited-talk"
 */
export function prefixToType(prefix: string): string {
  const mapping: Record<string, string> = {
    'Poster': 'poster',
    'Workshop': 'workshop',
    'Tutorial': 'tutorial',
    'Invited Talk': 'invited-talk',
  }
  return mapping[prefix] || prefix.toLowerCase()
}

/**
 * Build paper ID from URL params
 */
export function buildPaperId(type: string, id: string): string {
  return `${typeToPrefix(type)}-${id}`
}

/**
 * Parse paper ID into components
 */
export function parsePaperId(paperId: string): { type: string; id: string } | null {
  const match = paperId.match(/^(.+)-(\d+)$/)
  if (!match) return null
  return { type: match[1], id: match[2] }
}

/**
 * Get NeurIPS URL for a paper
 */
export function getNeuripsUrl(paperId: string): string {
  const parsed = parsePaperId(paperId)
  if (!parsed) return '#'
  const type = prefixToType(parsed.type)
  return `https://neurips.cc/virtual/2025/${type}/${parsed.id}`
}

/**
 * Get Paper2Song URL for a paper
 */
export function getPaperSongUrl(paperId: string): string {
  const parsed = parsePaperId(paperId)
  if (!parsed) return '/'
  const type = prefixToType(parsed.type)
  return `/virtual/2025/${type}/${parsed.id}`
}

